import type { MouseEvent } from "react";
import { useState } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { IconButton, OnOffSwitch, OnOffSwitchSize, PlainTooltip } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";
import useDeactivateOnOutsideClick from "app/pages/sales-intelligence/hooks/useDeactivateOnOutsideClick";

import { isSavedProfile } from "../../../helpers/common";
import type { Profile } from "../../../types/common";

import {
    StyledDeleteIconWrap,
    StyledEditableText,
    StyledEditIconWrap,
    StyledHeader,
    StyledHeaderControls,
    StyledHeaderInner,
    StyledHeaderLeft,
    StyledHeaderLeftInner,
    StyledHeaderToggleIconWrap,
    StyledToggleSwitchWrap,
} from "./styles";

type EmployeeProfileHeaderProps = {
    profile: Profile;
    isReadOnly?: boolean;
    onRename(newName: string): void;
    onExpandToggle(id: Profile["id"]): void;
    onRemoveClick(profile: Profile): void;
    onIsEnabledToggle(profile: Profile): void;
};

const CLASSNAME = "employee-profile-header-left";
const translate = i18nFilter();

const EmployeeProfileHeader = (props: EmployeeProfileHeaderProps) => {
    const { profile, isReadOnly = false, onExpandToggle, onIsEnabledToggle, onRemoveClick, onRename } = props;
    const [isNameBeingEdited, setIsNameBeingEdited] = useState(false);
    const isProfileEditable = isSavedProfile(profile) && !isReadOnly;

    useDeactivateOnOutsideClick(CLASSNAME, isNameBeingEdited, closeEditingDelayed);

    function handleRename(newName: string) {
        setIsNameBeingEdited(false);

        if (newName !== profile.name) {
            onRename(newName);
        }
    }

    function handleClick() {
        if (!isNameBeingEdited) {
            onExpandToggle(profile.id);
        }
    }

    function closeEditingDelayed() {
        setTimeout(() => setIsNameBeingEdited(false));
    }

    function stopPropagation(e: MouseEvent<HTMLDivElement>) {
        e.stopPropagation();
    }

    return (
        <StyledHeader>
            <StyledHeaderInner onClick={handleClick}>
                <StyledHeaderLeft overflow={!isNameBeingEdited}>
                    <StyledHeaderToggleIconWrap>
                        <SWReactIcons
                            iconName={profile.isExpanded ? "chev-up" : "chev-down"}
                            size="sm"
                        />
                    </StyledHeaderToggleIconWrap>
                    <StyledHeaderLeftInner
                        className={CLASSNAME}
                        overflow={!isNameBeingEdited}
                    >
                        <StyledEditableText
                            text={profile.name}
                            maxInputWidth={212}
                            isBeingEdited={isNameBeingEdited}
                            onSubmit={handleRename}
                            onCancel={() => setIsNameBeingEdited(false)}
                            overflow={!isNameBeingEdited}
                        />
                        {isProfileEditable && !isNameBeingEdited ? (
                            <PlainTooltip tooltipContent={translate("si.contacts-search-profiles.edit-dd.rename")}>
                                <StyledEditIconWrap>
                                    <IconButton
                                        type="flat"
                                        iconSize="xs"
                                        iconName="edit-icon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsNameBeingEdited(true);
                                        }}
                                        dataAutomation="quick-searches-overlay.saved-searches.list-item.option-rename"
                                    />
                                </StyledEditIconWrap>
                            </PlainTooltip>
                        ) : null}
                    </StyledHeaderLeftInner>
                </StyledHeaderLeft>
                <StyledHeaderControls onClick={stopPropagation}>
                    {isProfileEditable ? (
                        <PlainTooltip tooltipContent={translate("si.contacts-search-profiles.edit-dd.remove")}>
                            <StyledDeleteIconWrap>
                                <IconButton
                                    type="flat"
                                    iconSize="xs"
                                    iconName="delete"
                                    onClick={() => onRemoveClick(profile)}
                                    dataAutomation="quick-searches-overlay.saved-searches.list-item.option-delete"
                                />
                            </StyledDeleteIconWrap>
                        </PlainTooltip>
                    ) : null}
                    <StyledToggleSwitchWrap>
                        <OnOffSwitch
                            size={OnOffSwitchSize.small}
                            isSelected={profile.isEnabled}
                            onClick={() => {
                                onIsEnabledToggle(profile);
                            }}
                        />
                    </StyledToggleSwitchWrap>
                </StyledHeaderControls>
            </StyledHeaderInner>
        </StyledHeader>
    );
};

export default EmployeeProfileHeader;
